/**
 * Canvas Zoom Utilities
 *
 * Provides functions for calculating zoom-to-fit transformations for Fabric.js canvas.
 * Used to automatically zoom and pan the canvas to fit the garden boundary polygon
 * with configurable padding.
 */

/**
 * Calculate bounding box of polygon vertices
 *
 * @param {Array<{x: number, y: number}>} vertices - Polygon vertices
 * @returns {{minX: number, maxX: number, minY: number, maxY: number, width: number, height: number}}
 * @throws {Error} If vertices array is empty or null
 *
 * @example
 * const vertices = [{x: 0, y: 0}, {x: 100, y: 0}, {x: 100, y: 100}, {x: 0, y: 100}];
 * const bbox = calculateBoundingBox(vertices);
 * // Returns: {minX: 0, maxX: 100, minY: 0, maxY: 100, width: 100, height: 100}
 */
export const calculateBoundingBox = vertices => {
  if (!vertices || vertices.length === 0) {
    throw new Error('Cannot calculate bounding box: No vertices provided');
  }

  const xCoords = vertices.map(v => v.x);
  const yCoords = vertices.map(v => v.y);

  const minX = Math.min(...xCoords);
  const maxX = Math.max(...xCoords);
  const minY = Math.min(...yCoords);
  const maxY = Math.max(...yCoords);

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Add percentage-based padding to bounding box
 *
 * @param {{minX: number, maxX: number, minY: number, maxY: number, width: number, height: number}} boundingBox - Original bounding box
 * @param {number} paddingPercent - Padding as percentage (default: 0.1 for 10%)
 * @returns {{left: number, top: number, width: number, height: number}}
 *
 * @example
 * const bbox = {minX: 0, maxX: 100, minY: 0, maxY: 100, width: 100, height: 100};
 * const padded = addPadding(bbox, 0.1);
 * // Returns: {left: -10, top: -10, width: 120, height: 120}
 */
export const addPadding = (boundingBox, paddingPercent = 0.1) => {
  const paddingX = boundingBox.width * paddingPercent;
  const paddingY = boundingBox.height * paddingPercent;

  return {
    left: boundingBox.minX - paddingX,
    top: boundingBox.minY - paddingY,
    width: boundingBox.width + 2 * paddingX,
    height: boundingBox.height + 2 * paddingY,
  };
};

/**
 * Calculate zoom level and pan offset to fit bounding box in viewport
 *
 * Returns `null` for degenerate bounding boxes (missing, zero or negative
 * width/height) so callers can fall back to the default zoom without an
 * exception. Degenerate polygons can reach this function via collinear
 * vertices, corrupted saved outlines, or manual editing of a polygon to a line.
 *
 * @param {{left: number, top: number, width: number, height: number} | null | undefined} boundingBox - Padded bounding box
 * @param {number} canvasWidth - Canvas viewport width
 * @param {number} canvasHeight - Canvas viewport height
 * @returns {{zoom: number, panX: number, panY: number} | null} `null` when the bounding box is degenerate
 *
 * @example
 * const bbox = {left: 0, top: 0, width: 100, height: 100};
 * const { zoom, panX, panY } = calculateZoomToFit(bbox, 800, 600);
 * // Returns: {zoom: 6, panX: 100, panY: 0}
 */
export const calculateZoomToFit = (
  boundingBox,
  canvasWidth,
  canvasHeight
) => {
  if (!boundingBox || boundingBox.width <= 0 || boundingBox.height <= 0) {
    return null;
  }

  // Calculate zoom factors for width and height
  const zoomX = canvasWidth / boundingBox.width;
  const zoomY = canvasHeight / boundingBox.height;

  // Use smaller zoom to ensure entire box fits in viewport
  const zoom = Math.min(zoomX, zoomY);

  // Calculate center of bounding box
  const centerX = boundingBox.left + boundingBox.width / 2;
  const centerY = boundingBox.top + boundingBox.height / 2;

  // Calculate pan offset to center the bounding box
  const panX = canvasWidth / 2 - centerX * zoom;
  const panY = canvasHeight / 2 - centerY * zoom;

  return { zoom, panX, panY };
};

/**
 * Animate viewport transform (zoom + pan) with smooth transition
 *
 * Uses requestAnimationFrame for smooth 60fps animation with ease-in-out easing.
 *
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @param {number} targetZoom - Target zoom level
 * @param {{x: number, y: number}} targetPan - Target pan offset
 * @param {number} duration - Animation duration in milliseconds (default: 300)
 * @returns {Promise<void>} - Resolves when animation completes
 *
 * @example
 * await animateViewportTransform(canvas, 2, {x: 100, y: 50}, 300);
 */
export const animateViewportTransform = (
  canvas,
  targetZoom,
  targetPan,
  duration = 300
) => {
  return new Promise(resolve => {
    const startZoom = canvas.getZoom();
    const startPan = canvas.viewportTransform.slice(4, 6); // [panX, panY]
    const startTime = performance.now();

    const animate = currentTime => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-in-out quadratic function
      const easeInOutQuad = t => {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      };
      const easedProgress = easeInOutQuad(progress);

      // Interpolate zoom and pan
      const currentZoom =
        startZoom + (targetZoom - startZoom) * easedProgress;
      const currentPanX =
        startPan[0] + (targetPan.x - startPan[0]) * easedProgress;
      const currentPanY =
        startPan[1] + (targetPan.y - startPan[1]) * easedProgress;

      // Apply transform
      canvas.setZoom(currentZoom);
      canvas.viewportTransform[4] = currentPanX;
      canvas.viewportTransform[5] = currentPanY;
      canvas.requestRenderAll();

      // Continue animation or resolve
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(animate);
  });
};

/**
 * Convenience function: Zoom canvas to fit garden boundary
 *
 * Combines all zoom-to-fit steps into a single async function:
 * 1. Calculate bounding box from vertices
 * 2. Add padding
 * 3. Calculate zoom and pan
 * 4. Animate viewport transform
 *
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @param {Array<{x: number, y: number}>} vertices - Garden boundary vertices
 * @param {number} paddingPercent - Padding percentage (default: 0.1 for 10%)
 * @param {number} duration - Animation duration in ms (default: 300)
 * @returns {Promise<void>}
 *
 * @example
 * const vertices = [{x: 0, y: 0}, {x: 100, y: 0}, {x: 100, y: 100}, {x: 0, y: 100}];
 * await zoomToFitGarden(canvas, vertices, 0.1, 300);
 */
export const zoomToFitGarden = async (
  canvas,
  vertices,
  paddingPercent = 0.1,
  duration = 300
) => {
  // 1. Calculate bounding box
  const boundingBox = calculateBoundingBox(vertices);

  // 2. Add padding
  const paddedBox = addPadding(boundingBox, paddingPercent);

  // 3. Calculate zoom and pan
  const fit = calculateZoomToFit(
    paddedBox,
    canvas.getWidth(),
    canvas.getHeight()
  );

  // Degenerate outline (e.g. collinear vertices) → keep current viewport
  if (fit === null) {
    console.debug(
      'zoomToFitGarden: skipping auto-zoom (degenerate bounding box)'
    );
    return;
  }

  // 4. Animate viewport transform
  await animateViewportTransform(
    canvas,
    fit.zoom,
    { x: fit.panX, y: fit.panY },
    duration
  );
};
